import { UNASSIGNED_PARTNER_ID } from '@gooduncles/gu-app-schema';
import { Checkbox, Modal, Select, Tabs, notification } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { combineLatest } from 'rxjs';
import { LocalStorageKey } from 'src/constants';

import { errorObjectToString, getLocalStorageValue, setLocalStorageValue } from 'src/utils/common';
import {
  batchEnd,
  batchStart,
  observeDeliverySpot,
  observeStore,
  updateDeliverySpot,
} from 'src/utils/firebase/firebase-short-cut';

import { selectActiveDriverPartnersUsers } from 'src/redux/slices/partners';
import { useAppSelector } from 'src/redux/store';

import { useTitle } from 'src/hooks/useTitle';

import DeliverySpotWithShopTable, {
  DeliverySpotWithShopRowData,
  DeliverySpotWithShopTableRef,
} from './DeliverySpotWithShopTable';

type TabData = {
  key: string;
  label: string;
  partnerId?: string;
};

const ALL_TAB_KEY = 'all';
const UNDEFINED_PARTNER_SORT_KEY = 9999;
const PARTNER_CHANGE_SORT_KEY_OFFSET = 1000;

const DeliverySpotWithStore: FC = () => {
  useTitle('배송지 관리');
  const [rowData, setRowData] = useState<DeliverySpotWithShopRowData[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const [selectedSpotIds, setSelectedSpotIds] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const activePartners = useAppSelector(selectActiveDriverPartnersUsers);
  const tableRef = useRef<DeliverySpotWithShopTableRef>(null);

  const activeTabKey = searchParams.get('partner') || ALL_TAB_KEY;

  const tabItems = useMemo<TabData[]>(() => {
    const allTab: TabData = { key: ALL_TAB_KEY, label: '전체' };
    const partnerTabs = activePartners
      .sort((a, b) => a.deliveryPartnerStatus.sortKey - b.deliveryPartnerStatus.sortKey)
      .map((partner) => ({
        key: partner._id,
        label: partner.name,
        partnerId: partner._id,
      }));
    const unassignedTab: TabData = { key: UNASSIGNED_PARTNER_ID, label: '⚪️ 미지정', partnerId: UNASSIGNED_PARTNER_ID };
    return [allTab, ...partnerTabs, unassignedTab];
  }, [activePartners]);

  const filteredRowData = useMemo(() => {
    return rowData
      .filter((row) => showHidden || !row.hidden)
      .filter((row) => activeTabKey === ALL_TAB_KEY || row.partnerId === activeTabKey);
  }, [rowData, showHidden, activeTabKey]);

  // 미지정 탭은 동선 순서가 의미 없으므로 DnD 를 끈다.
  const enableDragAndDrop = activeTabKey !== ALL_TAB_KEY && activeTabKey !== UNASSIGNED_PARTNER_ID;

  const partnerOptions = useMemo(
    () => [
      ...activePartners
        .sort((a, b) => a.deliveryPartnerStatus.sortKey - b.deliveryPartnerStatus.sortKey)
        .map((partner) => ({
          label: `${partner.deliveryPartnerStatus.isActive ? '🟢' : '🔴'} ${partner.name} - ${
            partner.deliveryPartnerStatus.sortKey
          }`,
          value: partner._id,
        })),
      {
        label: '⚪️ 미지정',
        value: UNASSIGNED_PARTNER_ID,
      },
    ],
    [activePartners],
  );

  const onChangeShowHidden = (e: CheckboxChangeEvent) => {
    setLocalStorageValue(LocalStorageKey.delvierySpot, e.target.checked.toString());
    setShowHidden(e.target.checked);
  };

  const onSelectionChanged = useCallback((selectedIds: string[]) => {
    setSelectedSpotIds(selectedIds);
  }, []);

  const onSelectionClear = useCallback(() => {
    setSelectedSpotIds([]);
    tableRef.current?.clearSelection();
  }, []);

  const onTabChange = useCallback(
    (newActiveKey: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (newActiveKey === ALL_TAB_KEY) {
        newSearchParams.delete('partner');
      } else {
        newSearchParams.set('partner', newActiveKey);
      }
      setSearchParams(newSearchParams);
      onSelectionClear();
    },
    [searchParams, setSearchParams, onSelectionClear],
  );

  const onBulkPartnerChange = useCallback(
    async (newPartnerId: string) => {
      if (selectedSpotIds.length === 0) return;

      const selectedPartner = partnerOptions.find((option) => option.value === newPartnerId);
      const partnerName = selectedPartner?.label || '미지정';

      Modal.confirm({
        title: '담당 파트너 변경',
        content: `선택된 ${selectedSpotIds.length}개 배송지의 담당 파트너를 '${partnerName}'로 변경하시겠습니까?`,
        okText: '변경',
        cancelText: '취소',
        onOk: async () => {
          try {
            batchStart();
            for (const spotId of selectedSpotIds) {
              const currentSpot = rowData.find((row) => row._id === spotId);
              const currentSortKey = currentSpot?.sortKey || 0;
              const newSortKey = currentSortKey + PARTNER_CHANGE_SORT_KEY_OFFSET;

              await updateDeliverySpot(
                spotId,
                {
                  // 미지정도 센티넬 문자열로 쓴다 — undefined 는 ignoreUndefinedProperties 로 무시돼 기존 담당이 남았다(M-25).
                  partnerId: newPartnerId,
                  sortKey: newSortKey,
                  sortKeyForBadge: newSortKey,
                },
                { bBatch: true },
              );
            }
            await batchEnd();

            notification.success({
              message: '담당 파트너 변경 완료',
              description: `${selectedSpotIds.length}개 배송지의 담당 파트너가 변경되었습니다.`,
            });

            onSelectionClear();
          } catch (error) {
            console.error(error);
            const description = errorObjectToString(error);
            notification.error({
              message: '담당 파트너 변경에 실패했습니다.',
              description,
            });
          }
        },
      });
    },
    [selectedSpotIds, partnerOptions, onSelectionClear, rowData],
  );

  useEffect(() => {
    const storeObserver = observeStore();
    const deliverySpotObserver = observeDeliverySpot([['isDeleted', '!=', true]]);

    const subscription = combineLatest([storeObserver, deliverySpotObserver]).subscribe(
      ([storeDocs, deliverySpots]) => {
        const newData = deliverySpots
          .map((spot) => {
            const partner = activePartners.find((p) => p._id === spot.partnerId);
            return {
              ...spot,
              partnerId: spot.partnerId ?? UNASSIGNED_PARTNER_ID,
              partnerSortKey: partner?.deliveryPartnerStatus.sortKey ?? UNDEFINED_PARTNER_SORT_KEY,
              stores: storeDocs.filter((store) => store.deliverySpotId === spot._id),
            };
          })
          .sort((a, b) => a.sortKey - b.sortKey)
          .sort((a, b) => a.partnerSortKey - b.partnerSortKey);

        setRowData(newData);
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [activePartners]);

  useEffect(() => {
    const value = getLocalStorageValue<string>(LocalStorageKey.delvierySpot);
    setShowHidden(value === 'true');
  }, []);

  useEffect(() => {
    const partnerParam = searchParams.get('partner');
    const isValidPartner = partnerParam && tabItems.some((tab) => tab.key === partnerParam);
    const isAllTab = !partnerParam && activeTabKey === ALL_TAB_KEY;

    if (isValidPartner || isAllTab) {
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('partner');
    setSearchParams(newSearchParams, { replace: true });
  }, [activeTabKey, searchParams, setSearchParams, tabItems]);

  return (
    <div className='height100 flexColumn'>
      <div className='flexRow flexSpaceBetween flexAlignStart'>
        <Checkbox checked={showHidden} onChange={onChangeShowHidden}>
          숨김 항목 표시
        </Checkbox>
        {selectedSpotIds.length > 0 && (
          <div
            className='flexRow flexSpaceBetween flexAlignCenter'
            style={{
              padding: '6px 8px',
              backgroundColor: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: '6px',
            }}
          >
            <span style={{ fontWeight: 500 }}>{selectedSpotIds.length}개 배송지 선택됨</span>
            <Select
              placeholder='담당 파트너 변경'
              options={partnerOptions}
              onChange={onBulkPartnerChange}
              size='small'
              style={{ minWidth: '200px' }}
            />
          </div>
        )}
      </div>
      <Tabs
        activeKey={activeTabKey}
        onChange={onTabChange}
        tabBarStyle={{ marginBottom: 0 }}
        items={tabItems.map((tab) => ({ key: tab.key, label: tab.label }))}
      />
      {/* 탭마다 그리드를 두면 방문한 탭의 그리드가 계속 마운트된 채 같은 데이터로 다시 그려진다 — 활성 탭 하나만 그린다. */}
      <div style={{ flex: 1 }}>
        <DeliverySpotWithShopTable
          key={activeTabKey}
          ref={tableRef}
          rowData={filteredRowData}
          enableDragAndDrop={enableDragAndDrop}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
    </div>
  );
};

export default DeliverySpotWithStore;
